import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Amount } from '@welldev-group/react-comp-lib';
import Tooltip from '@welldev-group/rcl-tooltip';
import Icon from '@welldev-group/rcl-icon';
import Alert from '@welldev-group/rcl-alert';
import SanitizedHTML from '../../../../shared/components/SanitizedHTML';
import CreditCard from './CreditCard';

const PaymentSection = ({
  className,
  currentBalance,
  workflow,
  currency,
  paymentAlert,
  creditCard,
}) => {
  const isCurrentBalanceEmpty =
    parseFloat(currentBalance?.amountInPlanCurrency) <= 0;
  const hasEnoughBalanceToPay = workflow === 'balance_charge';

  return (
    <div className={classNames('payment-section', className)}>
      <div className="payment-section__title">
        {I18n.t('components.subscription.payment_title')}
      </div>
      <div className="payment-section__description">
        {I18n.t('components.subscription.payment_description')}
      </div>
      <div className="payment-section__current-balance">
        <div className="payment-section__current-balance-title">
          {I18n.t('components.subscription.current_balance_title')}
        </div>
        <div
          className={classNames('payment-section__current-balance-amount', {
            'payment-section__current-balance-amount--danger': isCurrentBalanceEmpty,
            'payment-section__current-balance-amount--warning':
              !isCurrentBalanceEmpty && !hasEnoughBalanceToPay,
            'payment-section__current-balance-amount--success': hasEnoughBalanceToPay,
          })}
        >
          <Amount
            minFractionDigit={2}
            currency={currency}
            value={currentBalance?.amountInPlanCurrency}
            returnStringOnly={true}
          />
          {currentBalance?.converted ? (
            <div className="payment-section__current-balance-amount-conversion-note">
              <Tooltip
                content={I18n.t(
                  'components.subscription.current_balance_conversion_note',
                  {
                    amount: Amount({
                      minFractionDigit: 2,
                      currency: currentBalance?.affiliateCurrency,
                      value: currentBalance?.amountInAffiliateCurrency,
                      currencyDisplay: 'code',
                      returnStringOnly: true,
                    }),
                    exchangeRate: currentBalance?.exchangeRate,
                  },
                )}
                position="top"
                type="inverse"
              >
                <Icon
                  name="highPriority"
                  size="tiny"
                  showBGColor={true}
                  color="tertiary"
                  rotate={180}
                />
              </Tooltip>
            </div>
          ) : null}
        </div>
      </div>
      <Alert
        className="payment-section__alert"
        isVisible={Boolean(paymentAlert)}
        hideClose={true}
        type="warning-light"
      >
        <SanitizedHTML html={paymentAlert} />
      </Alert>
      {creditCard && <CreditCard creditCard={creditCard} currency={currency} />}
    </div>
  );
};

PaymentSection.defaultProps = {
  className: '',
  workflow: null,
  currentBalance: {
    converted: false,
    amountInPlanCurrency: '',
    amountInAffiliateCurrency: '',
    affiliateCurrency: '',
    exchangeRate: '',
  },
  currency: 'USD',
  paymentAlert: '',
  creditCard: {
    cardType: '',
    cardholderName: '',
    lastFour: '',
  },
};

PaymentSection.propTypes = {
  className: PropTypes.string,
  currentBalance: PropTypes.shape({
    converted: PropTypes.bool,
    amountInPlanCurrency: PropTypes.string,
    amountInAffiliateCurrency: PropTypes.string,
    affiliateCurrency: PropTypes.string,
    exchangeRate: PropTypes.string,
  }),
  workflow: PropTypes.string,
  currency: PropTypes.string,
  paymentAlert: PropTypes.string,
  creditCard: PropTypes.shape({
    cardType: PropTypes.string,
    cardholderName: PropTypes.string,
    lastFour: PropTypes.string,
  }),
};

export default PaymentSection;
